import { Vector3 } from 'three';
import { AppStore } from '../state/store';
import { Section } from '../core/track/sections';
import {
  NodeSnapshot,
  scaleFromSnapshot,
  scalePointAbout,
  scaleSections,
  snapshotNodes,
} from '../state/commands';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { sidebarPane } from './sidebarTabs';

type OriginMode = 'world' | 'centroid' | 'first' | 'last';

const FACTOR_MIN = 0.25;
const FACTOR_MAX = 4;
const FACTOR_STEP = 0.01;

/**
 * Uniform scale of the whole track about a chosen origin. Factor is absolute
 * against the state captured when the drag started (1 = original); "Keep &
 * zero" bakes the current size so you can scale again from there.
 */
export class ScalePanel {
  private el: HTMLElement;
  private originMode: OriginMode = 'centroid';
  private factor = 1;

  private base: NodeSnapshot[] | null = null;
  private baseSections: Section[] | null = null;
  private baseStart = new Vector3();
  private origin = new Vector3();
  private applying = false;

  constructor(private store: AppStore) {
    this.el = document.createElement('div');
    sidebarPane('print').appendChild(this.el);

    store.on('doc', () => this.rebase());
    store.on('mode', () => this.rebase());
    store.on('track', () => {
      if (this.applying) return;
      this.dropBase();
      this.syncInputs();
    });
    this.render();
  }

  private capture(): boolean {
    const nodes = this.store.doc.track.nodes;
    if (nodes.length === 0) return false;
    this.base = snapshotNodes(nodes);
    this.baseSections = this.store.doc.sections.map((s) => ({ ...s }));
    this.baseStart.copy(this.store.doc.start.position);
    this.origin.copy(this.originFor(nodes));
    return true;
  }

  private originFor(nodes: typeof this.store.doc.track.nodes): Vector3 {
    switch (this.originMode) {
      case 'world':
        return new Vector3(0, 0, 0);
      case 'first':
        return nodes[0].p.clone();
      case 'last':
        return nodes[nodes.length - 1].p.clone();
      case 'centroid': {
        const c = new Vector3();
        for (const n of nodes) c.add(n.p);
        return c.divideScalar(nodes.length);
      }
      default: {
        const _exhaustive: never = this.originMode;
        return _exhaustive;
      }
    }
  }

  private dropBase(): void {
    this.base = null;
    this.baseSections = null;
    this.factor = 1;
  }

  private rebase(): void {
    this.dropBase();
    this.render();
  }

  private apply(): void {
    if (!this.base || !this.baseSections) return;
    const doc = this.store.doc;
    scaleFromSnapshot(doc.track.nodes, this.base, this.origin, this.factor);
    doc.sections = scaleSections(this.baseSections, this.factor);
    doc.start.position.copy(this.baseStart);
    scalePointAbout(doc.start.position, this.origin, this.factor);

    this.applying = true;
    this.store.updateGesture(null); // whole track moved — full rebuild
    this.applying = false;
  }

  private beginDrag(): boolean {
    if (!this.base && !this.capture()) return false;
    this.store.beginGesture();
    return true;
  }

  private render(): void {
    if (this.store.mode !== 'build') {
      this.el.innerHTML = '';
      return;
    }
    const n = this.store.doc.track.nodes.length;
    let html = panelStart('scale', 'Scale');
    if (n === 0) {
      html += `<div class="row"><label>No track to scale.</label></div>${panelEnd()}`;
      this.el.innerHTML = html;
      bindPanelCollapse(this.el);
      return;
    }

    html += `<div class="row"><label>Origin</label>
      <select data-key="origin">
        <option value="centroid"${sel(this.originMode === 'centroid')}>Centre</option>
        <option value="world"${sel(this.originMode === 'world')}>World (0,0,0)</option>
        <option value="first"${sel(this.originMode === 'first')}>First node</option>
        <option value="last"${sel(this.originMode === 'last')}>Last node</option>
      </select></div>`;
    html += `<div class="row"><label>Factor</label>
      <input type="range" data-key="factor" min="${FACTOR_MIN}" max="${FACTOR_MAX}" step="${FACTOR_STEP}" value="${this.factor}">
      <span class="val">${fmtFactor(this.factor)}</span></div>`;
    html += `<div class="row"><label>Exact</label>
      <input type="number" data-key="exact" min="${FACTOR_MIN}" max="${FACTOR_MAX}" step="${FACTOR_STEP}" value="${this.factor}"></div>`;
    html += `<div class="row">
      <button data-act="reset">Reset</button>
      <button data-act="bake">Keep &amp; zero</button>
      <button data-act="apply2">Apply ×2</button>
      <button data-act="applyHalf">Apply ×½</button></div>`;
    html += `<div class="hint">${n} node${n === 1 ? '' : 's'} · Scales positions, bank distances &amp; sections.
      Keep &amp; zero bakes the size so you can scale again.</div>`;
    html += panelEnd();

    this.el.innerHTML = html;
    bindPanelCollapse(this.el);
    this.wire();
  }

  private syncInputs(): void {
    const range = this.el.querySelector<HTMLInputElement>('input[data-key="factor"]');
    const exact = this.el.querySelector<HTMLInputElement>('input[data-key="exact"]');
    const val = this.el.querySelector('.val');
    if (range) range.value = String(this.factor);
    if (exact) exact.value = String(this.factor);
    if (val) val.textContent = fmtFactor(this.factor);
  }

  private setFactor(raw: number): void {
    const v = Math.min(FACTOR_MAX, Math.max(FACTOR_MIN, raw));
    // Avoid float noise on the slider (0.30000000004)
    this.factor = Math.round(v / FACTOR_STEP) * FACTOR_STEP;
  }

  /** Commit current preview, then treat the result as the new base at factor 1. */
  private bake(): void {
    this.dropBase();
    this.render();
  }

  /** One-shot multiply of the live track by `mult`, then rebase for another pass. */
  private applyOnce(mult: number): void {
    if (this.store.doc.track.nodes.length === 0) return;
    // If a preview is open, bake it first so ×2 compounds on the previewed size
    if (this.base && this.factor !== 1) {
      this.dropBase();
    }
    this.store.beginGesture();
    if (!this.capture()) {
      this.store.endGesture();
      return;
    }
    this.factor = mult;
    this.apply();
    this.store.endGesture();
    this.bake();
  }

  private wire(): void {
    const range = this.el.querySelector<HTMLInputElement>('input[data-key="factor"]');
    const exact = this.el.querySelector<HTMLInputElement>('input[data-key="exact"]');
    const valEl = this.el.querySelector('.val');

    range?.addEventListener('input', () => {
      if (!this.beginDrag()) return;
      this.setFactor(parseFloat(range.value));
      if (valEl) valEl.textContent = fmtFactor(this.factor);
      if (exact) exact.value = String(this.factor);
      this.apply();
    });
    range?.addEventListener('change', () => this.store.endGesture());

    exact?.addEventListener('change', () => {
      if (!this.beginDrag()) return;
      this.setFactor(parseFloat(exact.value) || 1);
      this.syncInputs();
      this.apply();
      this.store.endGesture();
    });

    this.el.querySelector<HTMLSelectElement>('select[data-key="origin"]')?.addEventListener('change', (e) => {
      this.originMode = (e.target as HTMLSelectElement).value as OriginMode;
      this.rebase();
    });

    this.el.querySelector<HTMLButtonElement>('button[data-act="reset"]')?.addEventListener('click', () => {
      if (!this.base) return;
      this.store.beginGesture();
      this.factor = 1;
      this.apply();
      this.store.endGesture();
      this.syncInputs();
    });
    this.el.querySelector<HTMLButtonElement>('button[data-act="bake"]')?.addEventListener('click', () => {
      this.bake();
    });
    this.el.querySelector<HTMLButtonElement>('button[data-act="apply2"]')?.addEventListener('click', () => {
      this.applyOnce(2);
    });
    this.el.querySelector<HTMLButtonElement>('button[data-act="applyHalf"]')?.addEventListener('click', () => {
      this.applyOnce(0.5);
    });
  }
}

function sel(on: boolean): string {
  return on ? ' selected' : '';
}

function fmtFactor(v: number): string {
  const s = v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return `${s}×`;
}
